/* eslint-disable no-param-reassign */
import { useMemo } from 'react';
import { cast, types } from 'mobx-state-tree';
import type {
  Instance,
  SnapshotIn,
  SnapshotOut,
} from 'mobx-state-tree/dist/core/type/type';

import { ApplicationModel, MentorFilterDataModel } from '@/models/applications';
import { initializeStore } from '@/stores';
import type { ApplicationRating } from '@/enums/applications';
import type {
  ApplicationModelType,
  MentorFilterDataModelType,
} from '@/models/applications';
import type { AdmissionStoreType } from '@/stores';

export const ApplicationStore = types
  .model({
    applications: types.array(ApplicationModel),
    mentorFilterData: types.maybe(MentorFilterDataModel),
    isStale: types.optional(types.boolean, false),
  })
  .views((self) => ({
    getApplicationByProject(
      projectId: number,
    ): ApplicationModelType | undefined {
      return self.applications.find((a) => a.project.pk === projectId);
    },
  }))
  .views((self) => ({
    getApplicationByPk(pk: number): ApplicationModelType {
      return self.applications.find((a) => a.pk === pk);
    },
  }))
  .actions((self) => ({
    // `admissionStore` below forces user to pass this argument, so all the related logic (stats update)
    // will be handles with application update. Probably store can be injected at initialization stage, to be
    // investigated and refactored if needed
    addApplication(
      application: ApplicationModelType,
      admissionStore: AdmissionStoreType,
    ) {
      if (
        application &&
        !self.applications.some((a) => a.pk === application.pk)
      ) {
        self.applications = cast([...self.applications, application]);
        admissionStore.increaseApplicationCounter(
          application.project?.season.pk,
        );
      }
    },
    removeApplication(
      application: ApplicationModelType,
      admissionStore: AdmissionStoreType,
    ) {
      const admissionId = application.project?.season.pk;
      self.applications = cast(
        self.applications.filter((a) => a.pk !== application.pk),
      );
      admissionStore.decreaseApplicationCounter(admissionId);
    },
    setApplications(applications: ApplicationModelType[]) {
      self.applications = cast(applications);
      self.isStale = false;
    },
    setMentorFilterData(mentorFilterData: MentorFilterDataModelType) {
      self.mentorFilterData = cast(mentorFilterData);
    },
    markStale() {
      self.isStale = true;
    },
    updateApplicationRatingAndComment(
      application: ApplicationModelType,
      rating: ApplicationRating,
      comment: string,
    ) {
      const app = self.getApplicationByPk(application.pk);
      app.rating = rating;
      app.mentorComment = comment;
    },
    updateApplicationScore(
      application: ApplicationModelType,
      score: number | null,
      checked: boolean,
    ) {
      if (checked) {
        const app = self.getApplicationByPk(application.pk);
        app.score = score;
        app.checked = checked;
      }
    },
  }))
  .named('ApplicationStore');

export const useApplicationStore = (
  initialState: ApplicationStoreSnapshotIn | null = null,
): ApplicationStoreType => {
  return useMemo(
    () => initializeStore(ApplicationStore, initialState),
    [initialState],
  );
};

export type ApplicationStoreType = Instance<typeof ApplicationStore> & {};

export type ApplicationStoreSnapshotIn = SnapshotIn<
  typeof ApplicationStore
> & {};

export type ApplicationStoreSnapshotOut = SnapshotOut<
  typeof ApplicationStore
> & {};
